// The curriculum as a ladder (spec 2026-06-19): drills become introduction RUNGS — one
// concept each, in introduction order — forming a vertical spine off root; apply tunes
// (grouped by identical `uses`) hang at the rung that completes their requirements. Placement
// is delegated to the pure skill-graph (buildLadder). Repertoire pieces bypass the ladder
// (parallel `rep:` tier, attached below). Pure leaf — gen-map-layout lays it out with dot.
import type { CatalogEntry } from "./catalog";
import { chordLabel, isChordAtom } from "./chord-atoms";
import { difficulty } from "./difficulty";
import { TEXTURE_RANK } from "./difficulty-tables";
import { atomTier } from "./introduction-order";
import { isScaleAtom } from "./scale-atoms";
import { type ApplySong, buildAcquisitionLattice, type RungInput } from "./skill-graph";
import { SKILLS } from "./skills";

// The skill prerequisite DAG (skills.ts) drives the technique axis, so edges follow real
// progressions (blocked-chords → broken-chords → alberti-bass) instead of a collapsed
// difficulty rank that conflates parallel skills.
const PREREQ = new Map(SKILLS.map((s) => [s.id, s.prereqs]));

// Skills that ride a tune for difficulty/identity but must NOT key the apply lattice. Two
// families: melodic character (RH scale/arpeggio) and chord-quality/progression *shapes*
// (triad/dom7/inversion/prog). The lattice nests by chord SET, not chord quality — these skills
// are taught by drills and gate mastery (curriculum-state), but they must not fork the apply
// chain (cohesive-corpus spec, A2). Excluding them also keeps the orphan guard / rank honest:
// a block tune with an arpeggio MELODY must not look like a rank-3 node.
const NON_STRUCTURAL_SKILLS = new Set<string>([
  "scale-major",
  "scale-minor",
  "arpeggios",
  "triad-major",
  "triad-minor",
  "dom7",
  "inversion-1",
  "inversion-2",
  "prog-I-IV-V",
  "prog-I-V-vi-IV",
  "prog-ii-V-I",
  "prog-12-bar-blues",
]);

const techSkills = (n: { requirements: string[] }): string[] =>
  n.requirements.filter((r) => !isChordAtom(r) && !NON_STRUCTURAL_SKILLS.has(r));

/** A piece is **repertoire-tier** (parallel track, not an apply-spine rung) when it
 *  carries no chord atoms, or carries chords but no *structural* texture skill (it
 *  expresses harmony through its own figuration, not block/broken/alberti). Shared by
 *  the lattice binning and the length-floor exemption (§3). */
export function isRepertoirePiece(entry: CatalogEntry): boolean {
  const uses = (entry.uses ?? []).filter((u) => !isScaleAtom(u));
  // A five-finger melody (e.g. a one-finger pedal-drone tune) is a beginner SPINE entry, not a
  // transcription — it belongs at the five-finger tier, ahead of any chord.
  if (uses.includes("five-finger")) return false;
  const chords = uses.filter(isChordAtom);
  const hasStructuralTech = uses.some((u) => u in TEXTURE_RANK && !NON_STRUCTURAL_SKILLS.has(u));
  return chords.length === 0 || !hasStructuralTech;
}

// Short edge labels per technique skill id (so an edge names the *actual* skill it
// adds — "alberti" / "broken", not a merged rank label). Chord atoms use chordLabel.
const SKILL_SHORT: Record<string, string> = {
  "five-finger": "five-finger",
  "scale-major": "scale",
  "blocked-chords": "block",
  "triad-major": "triad",
  "inversion-1": "inv 1",
  "prog-I-IV-V": "I–IV–V",
  "prog-I-V-vi-IV": "I–V–vi–IV",
  "broken-chords": "broken",
  "alberti-bass": "alberti",
  arpeggios: "arpeggio",
  "hand-independence": "hands",
};
const atomShort = (atom: string): string =>
  isChordAtom(atom) ? chordLabel(atom) : (SKILL_SHORT[atom] ?? atom);

// Connectivity (ladder model): the spine is a single methodical column off root, so the old
// ROOT_FANOUT ≥ 2 invariant is obsolete (root legitimately has one entry rung). What must hold
// instead is reachability — every non-root node is reachable from root via forward edges.
interface ConnectivityViolation {
  id: string;
  kind: "unreachable";
  detail: string;
}

export function connectivityViolations(lattice: SongLattice): ConnectivityViolation[] {
  const adj = new Map<string, string[]>();
  for (const ed of lattice.edges) {
    const list = adj.get(ed.from);
    if (list) list.push(ed.to);
    else adj.set(ed.from, [ed.to]);
  }
  const seen = new Set<string>(["root"]);
  const stack = ["root"];
  while (stack.length) {
    const cur = stack.pop();
    if (cur === undefined) continue;
    for (const to of adj.get(cur) ?? []) {
      if (!seen.has(to)) {
        seen.add(to);
        stack.push(to);
      }
    }
  }
  return lattice.nodes
    .filter((n) => !n.isRoot && !seen.has(n.id))
    .map((n) => ({
      id: n.id,
      kind: "unreachable" as const,
      detail: `${n.id} is unreachable from root`,
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
}

export interface LatticeNode {
  id: string; // bundle key, or "root" / "rep:*"
  label: string; // a representative song title
  songIds: string[];
  requirements: string[]; // the full `uses` atom set
  chords: string[]; // chord atoms only
  techniqueRank: number;
  difficulty: number;
  teaches: string[]; // derived placement delta (the skills this node introduces)
  isRoot?: boolean;
}
export interface LatticeEdge {
  from: string;
  to: string;
  label: string;
  kind: "technique" | "chord" | "root";
  /** Raw atom ids this edge introduces (technique skill(s) or chord atom(s)) — the
   *  drills that teach these are what the discovery-map panel reveals for the edge. */
  adds: string[];
  delta: string[]; // the ≤STEP_CAP skills this edge introduces (== adds for graph edges)
}
export interface SongLattice {
  nodes: LatticeNode[];
  edges: LatticeEdge[];
}

// One rung per distinct drill `teaches`-set (scale atoms stripped, so C/F/G scale étude
// variants collapse to one `scale-major` rung). The canonical drill is the lowest-difficulty,
// then lowest-id member. `atom` is the set's most *distinctive* member (rarest across sets,
// chord-first) so the rung id is unique and reads naturally — e.g. the contrary-motion drill
// `{five-finger, hand-independence}` keys on `hand-independence`, not the shared `five-finger`.
export function rungInputsFromDrills(catalog: readonly CatalogEntry[]): RungInput[] {
  const drills = catalog.filter((e) => (e.uses?.length ?? 0) === 0 && (e.teaches?.length ?? 0) > 0);
  const introducesOf = (d: CatalogEntry): string[] =>
    [...new Set(d.teaches ?? [])].filter((a) => !isScaleAtom(a)).sort();
  const bySet = new Map<string, CatalogEntry[]>();
  for (const d of drills) {
    if (introducesOf(d).length === 0) continue; // pure scale-atom drills don't gate the spine
    const k = introducesOf(d).join("+");
    const members = bySet.get(k);
    if (members) members.push(d);
    else bySet.set(k, [d]);
  }
  // How many distinct sets each atom appears in — the rarer, the more distinctive.
  const freq = new Map<string, number>();
  for (const k of bySet.keys()) for (const a of k.split("+")) freq.set(a, (freq.get(a) ?? 0) + 1);
  const primaryAtom = (introduces: readonly string[]): string =>
    [...introduces].sort(
      (a, b) =>
        (freq.get(a) ?? 0) - (freq.get(b) ?? 0) ||
        Number(isChordAtom(b)) - Number(isChordAtom(a)) ||
        a.localeCompare(b),
    )[0] ?? "";
  const rungs: RungInput[] = [];
  for (const members of bySet.values()) {
    const canonical = [...members].sort(
      (a, b) => difficulty(a) - difficulty(b) || a.id.localeCompare(b.id),
    )[0];
    if (!canonical) continue;
    const introduces = introducesOf(canonical);
    rungs.push({ atom: primaryAtom(introduces), drillId: canonical.id, introduces });
  }
  return rungs.sort((a, b) => a.atom.localeCompare(b.atom));
}

export function buildSongLattice(catalog: readonly CatalogEntry[]): SongLattice {
  const apply = catalog.filter((e) => (e.uses?.length ?? 0) > 0);
  const entryOf = new Map(catalog.map((e) => [e.id, e]));
  // Scale-atom blockers gate but don't define a node — they covary with difficulty and would
  // fragment the technique/chord axes. The lattice ignores them (the gate lives in
  // curriculum-state; routing to scale drills is the sequencer's job).
  const usesOf = (e: CatalogEntry): string[] => (e.uses ?? []).filter((u) => !isScaleAtom(u));
  const chordsOf = (e: CatalogEntry): string[] =>
    [...new Set(usesOf(e).filter(isChordAtom))].sort();
  const trankOf = (e: CatalogEntry): number =>
    Math.max(
      0,
      ...usesOf(e)
        .filter((u) => !isChordAtom(u) && !NON_STRUCTURAL_SKILLS.has(u))
        .map((u) => TEXTURE_RANK[u] ?? 0),
    );
  const labelOf = (id: string): string => (entryOf.get(id)?.title ?? id).replace(/ —.*/, "");

  // The apply spine is an acquisition lattice: drills become introduction rungs; a node is the
  // set of atoms acquired so far; same-tier peers branch (≤3 choices) and re-converge; a tune
  // lives on the shallowest state covering its `uses`. Priority = atomTier (hard order) then
  // corpus frequency (soft peer rank). Repertoire pieces bypass the lattice (parallel tier).
  const spineEntries = apply.filter((e) => !isRepertoirePiece(e));
  const chordRank = (() => {
    const f = new Map<string, number>();
    for (const en of apply)
      for (const u of usesOf(en)) if (isChordAtom(u)) f.set(u, (f.get(u) ?? 0) + 1);
    const ordered = [...f.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([id]) => id);
    return new Map(ordered.map((id, i) => [id, i]));
  })();
  const INTRO_GRADE = new Map(SKILLS.map((s) => [s.id, s.introGrade]));
  const priorityOf = (atom: string): readonly [number, number, string] => [
    atomTier(atom),
    isChordAtom(atom) ? (chordRank.get(atom) ?? 999) : (INTRO_GRADE.get(atom) ?? 99),
    atom,
  ];
  const rungInputs = rungInputsFromDrills(catalog);
  const applySongs: ApplySong[] = spineEntries.map((e) => ({
    id: e.id,
    uses: [...new Set(usesOf(e))],
  }));
  const graph = buildAcquisitionLattice(rungInputs, applySongs, {
    priorityOf,
    prereqsAtom: (atom) => PREREQ.get(atom) ?? [],
    cap: 3,
  });

  // Map each SkillNode → LatticeNode. Determinism: songIds are already sorted by the graph;
  // label/difficulty are canonicalized from the first (sorted) member, so output is
  // independent of catalog order.
  const spineNodes: LatticeNode[] = graph.nodes
    .filter((n) => !n.isRoot)
    .map((n) => {
      const first = entryOf.get(n.songIds[0] ?? "");
      return {
        id: n.id,
        label: labelOf(n.songIds[0] ?? ""),
        songIds: [...n.songIds],
        requirements: [...n.uses],
        chords: [...n.uses].filter(isChordAtom).sort(),
        techniqueRank: first ? trankOf(first) : 0,
        difficulty: first ? difficulty(first) : 1,
        teaches: [...n.teaches],
      };
    })
    .sort((a, b) => a.id.localeCompare(b.id)); // determinism: node order independent of catalog order

  // Repertoire pieces bin by their technique `uses` into a parallel `rep:` tier (a transcription
  // has no place on the chord ladder); they attach to the simplest folk node teaching their
  // hardest prerequisite technique, not the root.
  const repBins = new Map<string, LatticeNode>();
  for (const ent of apply.filter(isRepertoirePiece)) {
    const key = [...new Set(usesOf(ent))].sort().join("+");
    const node = repBins.get(key);
    if (node) node.songIds.push(ent.id);
    else
      repBins.set(key, {
        id: `rep:${key}`,
        label: ent.title.replace(/ —.*/, ""),
        songIds: [ent.id],
        requirements: [...new Set(usesOf(ent))].sort(),
        chords: chordsOf(ent),
        techniqueRank: trankOf(ent),
        difficulty: difficulty(ent),
        teaches: [],
      });
  }
  const repNodes = [...repBins.values()];
  for (const n of repNodes) {
    n.songIds.sort();
    const first = entryOf.get(n.songIds[0] ?? "");
    n.label = (first?.title ?? n.label).replace(/ —.*/, "");
    if (first) n.difficulty = difficulty(first);
  }
  repNodes.sort((a, b) => a.difficulty - b.difficulty || a.id.localeCompare(b.id));

  const rootNode: LatticeNode = {
    id: "root",
    label: "Start",
    songIds: [],
    requirements: [],
    chords: [],
    techniqueRank: 0,
    difficulty: 1,
    teaches: [],
    isRoot: true,
  };
  const out: LatticeNode[] = [rootNode, ...spineNodes, ...repNodes];

  // Map each SkillEdge → LatticeEdge: a `root` edge from the entry state, else a `chord` edge
  // when the whole delta is chord atoms, else a `technique` edge. `adds`/`delta` both carry the
  // ≤STEP_CAP delta; the label is the short names of the delta.
  const edges: LatticeEdge[] = graph.edges.map((e) => {
    const allChords = e.delta.length > 0 && e.delta.every(isChordAtom);
    const kind: LatticeEdge["kind"] =
      e.from === "root" ? "root" : allChords ? "chord" : "technique";
    return {
      from: e.from,
      to: e.to,
      label: e.delta.map(atomShort).join(",") || "·",
      kind,
      adds: [...e.delta],
      delta: [...e.delta],
    };
  });

  // Repertoire tier: a real piece hangs off the folk node where its hardest prerequisite
  // technique is *learned* — never off the root (you can't jump from the five-finger drill to
  // a Schumann piece). Walk each required skill's prereq closure to the hardest skill the folk
  // tree teaches, and attach to the simplest folk node that has it.
  const folkSkills = new Set(spineNodes.flatMap((n) => techSkills(n)));
  const closureOf = (skill: string): string[] => {
    const out2 = new Set<string>();
    const stack = [skill];
    while (stack.length) {
      const s = stack.pop();
      if (s === undefined || out2.has(s)) continue;
      out2.add(s);
      for (const p of PREREQ.get(s) ?? []) stack.push(p);
    }
    return [...out2];
  };
  for (const r of repNodes) {
    const reachable = [...new Set(r.requirements.flatMap(closureOf))].filter((s) =>
      folkSkills.has(s),
    );
    const anchorSkill = reachable.sort(
      (a, b) => (TEXTURE_RANK[b] ?? 0) - (TEXTURE_RANK[a] ?? 0) || a.localeCompare(b),
    )[0];
    const host = anchorSkill
      ? spineNodes
          .filter((n) => n.requirements.includes(anchorSkill))
          .sort((a, b) => a.difficulty - b.difficulty || a.id.localeCompare(b.id))[0]
      : undefined;
    edges.push({
      from: host?.id ?? "root",
      to: r.id,
      label: "♪",
      kind: "technique",
      adds: r.requirements,
      delta: r.requirements,
    });
  }
  return { nodes: out, edges };
}
